/**
 * Design constants for Activity Details screen
 * Colors and spacing values from Figma design
 */

import { HEADER_HEIGHT } from "../home-v2/HomeV2Header";

export const ACTIVITY_DETAILS_COLORS = {
  // Primary colors
  primaryBlue: '#01A7C8',
  darkBlue: '#007CAD',
  lightestBlue: '#DBF7F8',
  lighterBlue: '#C7EDEF',
  
  // Grayscale
  gray6: '#1B2831',
  gray5: '#323B46',
  gray4: '#4D5762',
  gray3: '#A9B7BE',
  white: '#FFFFFF',
  
  // Semantic
  background: '#01A7C8',
  surface: '#FFFFFF',
  textPrimary: '#1B2831',
  textSecondary: '#4D5762',
  textBody: '#323B46',
  tagText: '#007CAD',
  tagDivider: '#A9B7BE',
  infoBoxBg: '#DBF7F8',
  infoBoxText: '#007CAD',
  feedbackBorder: '#C7EDEF',
  stepNumberBg: '#01A7C8',
  stepNumberText: '#DBF7F8',
  sectionHeader: '#01A7C8',
} as const;

export const ACTIVITY_DETAILS_SPACING = {
  pagePadding: 24,
  sectionGap: 16,
  elementGap: 8,
  cardPadding: 16,
  buttonHeight: 48,
  stepNumberSize: 24,
  iconSizeSmall: 14,
  iconSizeMedium: 24,
  imageHeight: 197,
  cardImageHeight: 180,
  recommendationCardWidth: 312,
  headerHeight: HEADER_HEIGHT, // HomeV2Header fixed height - matches HOME_V2_HEADER_HEIGHT
  topBarHeight: 52, // ActivityTopBar height
} as const;

export const ACTIVITY_DETAILS_RADIUS = {
  container: 24,
  image: 12,
  button: 8,
  card: 24,
  stepBadge: 12,
  infoBox: 16,
  likeBadge: 12,
} as const;

export const ACTIVITY_DETAILS_SHADOWS = {
  card: '0px 4px 16px rgba(0, 124, 173, 0.2)',
  likeBadge: '0px 0px 24px rgba(0, 0, 0, 0.25)',
} as const;

/**
 * Responsive spacing utilities for Activity Details
 * Scales based on Tailwind breakpoints: default, sm (640px), md (768px), lg (1024px), xl (1280px)
 */
export const ACTIVITY_DETAILS_RESPONSIVE = {
  // Container max widths
  containerMaxWidth: {
    default: '448px',  // max-w-md (mobile)
    md: '768px',       // Tablet portrait
    lg: '896px',       // Laptop/desktop
    xl: '1024px',      // Large desktop
  },
  
  // Page horizontal padding (scales up with screen size)
  pagePaddingClasses: 'px-6 sm:px-8 md:px-10 lg:px-12',
  
  // Horizontal padding for Swiper/carousels
  horizontalPadding: 'px-6 sm:px-8 md:px-10 lg:px-12',
  
  // Horizontal margin (for sections with background boxes)
  horizontalMargin: 'mx-6 sm:mx-8 md:mx-10 lg:mx-12',
  
  // Section vertical spacing
  sectionGapClasses: 'gap-4 sm:gap-5 md:gap-6',
  
  // Element spacing
  elementGapClasses: 'gap-2 sm:gap-2.5 md:gap-3',
  
  // Image heights (maintains aspect ratio but can scale)
  imageHeightClasses: 'h-[197px] md:h-[240px] lg:h-[280px]',
  
  // Button heights (minimum touch target 44px)
  buttonHeightClasses: 'h-12 md:h-14',  // 48px → 56px
} as const;

/**
 * Responsive typography utilities
 * Base sizes increase by 2px at md (tablet), 4px at lg/xl (desktop)
 */
export const ACTIVITY_DETAILS_TYPOGRAPHY = {
  // Title: 32px → 34px → 36px
  title: {
    sizeClasses: 'text-[32px] md:text-[34px] lg:text-[36px]',
    weight: 'font-bold',
    tracking: 'tracking-[-0.04em]',  // -4%
    leading: 'leading-none',
  },
  
  // Section headers: 20px → 22px → 24px
  sectionHeader: {
    sizeClasses: 'text-[20px] md:text-[22px] lg:text-[24px]',
    weight: 'font-semibold',
    tracking: 'tracking-[-0.04em]',  // -4%
  },
  
  // Subsection headers: 18px → 20px → 22px
  subsectionHeader: {
    sizeClasses: 'text-[18px] md:text-[20px] lg:text-[22px]',
    weight: 'font-bold',
    tracking: 'tracking-[-0.04em]',  // -4%
  },
  
  // Body text: 16px → 18px → 20px
  body: {
    sizeClasses: 'text-base md:text-lg lg:text-xl',  // 16px → 18px → 20px
    weight: 'font-normal',
    leading: 'leading-7',  // 28px line height
  },
  
  // Metadata/small text: 14px → 15px → 16px
  metadata: {
    sizeClasses: 'text-sm md:text-[15px] lg:text-base',  // 14px → 15px → 16px
    weight: 'font-normal',
  },
  
  // Caption/tags: 12px → 13px → 14px
  caption: {
    sizeClasses: 'text-xs md:text-[13px] lg:text-sm',  // 12px → 13px → 14px
    weight: 'font-medium',
  },
  
  // Button text: 12px (stays same for consistency)
  button: {
    sizeClasses: 'text-xs',  // 12px across all breakpoints
    weight: 'font-black',
    tracking: 'tracking-[0.06em]',  // 6%
    transform: 'uppercase',
  },
  
  // Feedback section title: 18px → 20px → 22px
  feedbackTitle: 'text-[18px] md:text-[20px] lg:text-[22px]',
  
  // Feedback button label: 14px → 15px → 16px
  feedbackButtonLabel: 'text-sm md:text-[15px] lg:text-base',
  
  // Thank you message: 12px → 13px → 14px
  thankYouMessage: 'text-xs md:text-[13px] lg:text-sm',
  
  // Recommendations title: 18px → 20px → 22px
  recommendationsTitle: 'text-[18px] md:text-[20px] lg:text-[22px]',
  
  // Recommendation card title: 20px → 22px → 24px
  recommendationCardTitle: 'text-[20px] md:text-[22px] lg:text-[24px]',
  
  // Recommendation card description: 14px → 15px → 16px
  recommendationCardDescription: 'text-sm md:text-[15px] lg:text-base',
  
  // Recommendation card metadata: 12px → 13px → 14px
  recommendationCardMetadata: 'text-xs md:text-[13px] lg:text-sm',
  
  // Top bar text: 12px → 13px → 14px
  topBarRecommendedText: 'text-xs md:text-[13px] lg:text-sm',
  topBarDoneButton: 'text-xs md:text-[13px] lg:text-sm',
} as const;
